'use client'

import { ReactNode, useEffect, useMemo, useRef, useState } from "react"
import ReactDOMServer from "react-dom/server"

type ParsedCmsContent = {
  styles: string
  scripts: string
  body: string
}

function parseHtml(html: string): ParsedCmsContent {
  const stylePattern = /<style[^>]*>[\s\S]*?<\/style>|<link[^>]+rel=["']stylesheet["'][^>]*\/?>/gi
  const scriptPattern = /<script[^>]*>[\s\S]*?<\/script>/gi

  const styles = [...html.matchAll(stylePattern)].map(m => m[0]).join('\n')
  const scripts = [...html.matchAll(scriptPattern)].map(m => m[0]).join('\n')

  const body = html
    .replace(stylePattern, '')
    .replace(scriptPattern, '')
    .trim()

  return { styles, scripts, body } as const
}

export function SandboxContent({ children }: { children: ReactNode }) {
  const observersRef = useRef<{
    resize?: ResizeObserver;
    mutation?: MutationObserver;
    cleanup?: (() => void)[]
  }>({})

  const [isMounted, setIsMounted] = useState(false)

  const html = useMemo(() => ReactDOMServer.renderToString(children), [children])

  useEffect(() => {
    setIsMounted(true)

    return () => {
      observersRef.current.resize?.disconnect()
      observersRef.current.mutation?.disconnect()
      observersRef.current.cleanup?.forEach(fn => fn())
    }
  }, [])

  const resize = (iframe: HTMLIFrameElement) => {
    const iframeDoc = iframe.contentWindow?.document

    if (!iframeDoc) {
      return
    }

    const height = iframeDoc.body.scrollHeight

    if (height) {
      iframe.style.height = `${height}px`
    }
  }

  const fixLinks = (iframeDoc: Document) => {
    iframeDoc.querySelectorAll('a').forEach(a => {
      const href = a.getAttribute('href')

      if (!href) return

      if (href.startsWith('#')) {
        // Intercept and manually scroll within iframe
        a.addEventListener('click', (e) => {
          e.preventDefault()
          const target = iframeDoc.querySelector(href)
          target?.scrollIntoView({ behavior: 'smooth' })
        })
        return
      }
      a.target = '_parent'
    })
  }

  const handleLoad = (iframe: HTMLIFrameElement) => {
    const iframeWin = iframe.contentWindow
    const iframeDoc = iframeWin?.document

    if (!iframeWin || !iframeDoc) {
      return
    }

    iframeDoc.documentElement.style.overflow = 'hidden'
    iframeDoc.body.style.overflow = 'hidden'
    iframeDoc.body.style.margin = '0'

    resize(iframe)

    const resizeObserver = new ResizeObserver(() => resize(iframe))

    resizeObserver.observe(iframeDoc.body)
    observersRef.current.resize = resizeObserver

    fixLinks(iframeDoc)

    const mutationObserver = new MutationObserver(() => fixLinks(iframeDoc))

    mutationObserver.observe(iframeDoc.body, { childList: true, subtree: true })
    observersRef.current.mutation = mutationObserver
  }

  const { styles, body, scripts } = parseHtml(html)

  const srcdoc = `
   <!DOCTYPE html>
    <html>
      <head>
        ${styles}
      </head>
      <body>
        ${body}
        ${scripts}
      </body>
    </html>`

  const handleIframeRef = (iframe: HTMLIFrameElement | null) => {
    if (!iframe) {
      return
    }

    const handleLoadEvent = () => handleLoad(iframe)
    iframe.addEventListener('load', handleLoadEvent)

    const cleanup = () => {
      iframe.removeEventListener('load', handleLoadEvent)
    }

    if (!observersRef.current.cleanup) {
      observersRef.current.cleanup = []
    }

    observersRef.current.cleanup.push(cleanup)
  }

  return (
    <>
      {isMounted ? (
        <iframe
          ref={handleIframeRef}
          srcDoc={srcdoc}
          sandbox="
          allow-scripts 
          allow-same-origin 
          allow-forms 
          allow-top-navigation-by-user-activation"
          style={{
            width: '100%',
            height: '100vh',
            border: 'none',
            display: 'block',
            overflow: 'hidden',
          }}
        />
      ) : (
        <div
          data-cms-seo-content
          dangerouslySetInnerHTML={{ __html: html }}
        />
      )}
    </>
  )
}
